export const PAYMENT_UNAVAILABLE = "This estate is not ready for payments yet. Please contact your estate administrator.";
export const PARTIAL_COUPON_BANK_MESSAGE = "Partial coupon discounts are currently available with online payment. Choose Pay Online or remove the coupon to continue with bank transfer.";
export const persistedAccountIdentity = (account) => account ? ({ type: account.type, estateId: account.estateId, name: account.name, isPrimary: account.isPrimary }) : null;

// Resident-safe wording for the server's stable blocking-reason codes
// (CONTRACT.md). Unknown codes are never shown raw.
export const BLOCKING_REASON_MESSAGES = {
  ESTATE_NOT_FOUND: "We could not find this estate.",
  ALLOWED_COMMODITY_NOT_CONFIGURED: "This commodity is not enabled for your estate.",
  WATER_RATE_NOT_CONFIGURED: "The water rate has not been set for this estate yet.",
  ELECTRICITY_RATE_NOT_CONFIGURED: "The electricity rate has not been set for this estate yet.",
  GAS_RATE_NOT_CONFIGURED: "The gas rate has not been set for this estate yet.",
  PAYMENT_MODE_INVALID: "The estate's payment method setup is invalid.",
  PAYSTACK_CONNECTION_MODE_NOT_SELECTED: "Online payment setup is not finished.",
  PAYSTACK_CONNECTION_MODE_INVALID: "Online payment setup is invalid.",
  PAYSTACK_PLATFORM_UNAVAILABLE: "Online payment is temporarily unavailable.",
  PAYSTACK_SUBACCOUNT_MISSING: "Online payment setup is not finished.",
  ESTATE_PAYSTACK_CREDENTIALS_MISSING: "Online payment setup is not finished.",
  ESTATE_PAYSTACK_CREDENTIALS_UNREADABLE: "Online payment setup needs attention.",
  ESTATE_PAYSTACK_TEST_REQUIRED: "Online payment setup is awaiting verification.",
  ESTATE_PAYSTACK_TEST_FAILED: "Online payment setup needs attention.",
  BANK_DETAILS_INCOMPLETE: "Bank transfer details are incomplete.",
};

export const blockingReasonMessages = (reasons) => {
  const out = [];
  (Array.isArray(reasons) ? reasons : []).forEach((code) => {
    const msg = BLOCKING_REASON_MESSAGES[code];
    if (msg && !out.includes(msg)) out.push(msg);
  });
  return out;
};

const RATE_REASON_FOR = {
  water: ["WATER_RATE_NOT_CONFIGURED", "ALLOWED_COMMODITY_NOT_CONFIGURED", "ESTATE_NOT_FOUND"],
  electricity: ["ELECTRICITY_RATE_NOT_CONFIGURED", "ALLOWED_COMMODITY_NOT_CONFIGURED", "ESTATE_NOT_FOUND"],
  gas: ["GAS_RATE_NOT_CONFIGURED", "ALLOWED_COMMODITY_NOT_CONFIGURED", "ESTATE_NOT_FOUND"],
};

// Availability is decided by /users/estates. Missing fields fail closed. The
// client never looks at the Paystack connection mode (OVEND_SUBACCOUNT vs
// ESTATE_DIRECT) — only at the server's *Available flags.
export const residentPaymentSettings = (settings = {}) => {
  settings = settings || {};
  const paymentMode = settings.paymentMode || "PAYSTACK";
  const valid = ["PAYSTACK", "BANK_TRANSFER", "BOTH"].includes(paymentMode);
  const ready = settings.readyForResidentPayments !== false; // explicit false => blocked
  const paystackAvailable = ready && valid && paymentMode !== "BANK_TRANSFER" && settings.paystackAvailable === true;
  const bankTransferAvailable = ready && valid && paymentMode !== "PAYSTACK" && settings.bankTransferAvailable === true;
  const rails = [paystackAvailable && "PAYSTACK", bankTransferAvailable && "BANK_TRANSFER"].filter(Boolean);
  return {
    paymentMode,
    globalPaystackAvailable: settings.globalPaystackAvailable === true,
    paystackConfigured: settings.paystackConfigured === true,
    bankTransferConfigured: settings.bankTransferConfigured === true,
    paystackAvailable,
    bankTransferAvailable,
    rails,
    readyForResidentPayments: rails.length > 0,
    blockingReasons: Array.isArray(settings.blockingReasons) ? settings.blockingReasons : [],
    bankTransfer: bankTransferAvailable ? settings.bankTransfer || null : null,
  };
};

// Everything a resident account needs from a /users/estates membership's
// populated estate: payment rails, authoritative rates, units and readiness.
// Rebuilt wholesale on every estate switch/refresh so nothing leaks across estates.
export const residentEstateConfig = (estate) => ({
  ...residentPaymentSettings(estate?.paymentSettings),
  commodityRate: estate?.commodityRate && typeof estate.commodityRate === "object" ? estate.commodityRate : undefined,
  commodityUnits: estate?.commodityUnits || undefined,
  readiness: estate?.readiness || undefined,
});

// Authoritative server rate for a commodity. `authoritative` is true when the
// estate payload carried rate information at all (then a missing/0 rate means
// "Rate unavailable" — there is no client-side fallback rate).
export const resolveServerRate = (account, commodity) => {
  const key = String(commodity || "").toLowerCase();
  const fromRate = account?.commodityRate;
  const fromReadiness = account?.readiness?.commodities?.[key];
  const authoritative = !!fromRate || !!fromReadiness;
  const candidates = [fromRate?.[key], fromReadiness?.rate].map(Number);
  const rate = candidates.find((n) => Number.isFinite(n) && n > 0) || 0;
  const reasons = [
    ...(account?.readiness?.blockingReasons || []),
    ...(account?.blockingReasons || []),
  ].filter((c) => (RATE_REASON_FOR[key] || []).includes(c));
  return { authoritative, rate, reasons: Array.from(new Set(reasons)) };
};
